(() => {
  if (customElements.get('rr-collection-feed')) return;

  class RiotCollectionFeed extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        this.observe();
        return;
      }
      this.grid = this.querySelector('[data-feed-grid]');
      this.moreButton = this.querySelector('[data-feed-more]');
      this.previousButton = this.querySelector('[data-feed-previous]');
      this.previousWrap = this.querySelector('[data-feed-previous-wrap]');
      this.status = this.querySelector('[data-feed-status]');
      this.controls = this.querySelector('[data-feed-controls]');
      if (!this.grid || !this.moreButton || !this.previousButton || !this.status) return;
      this.initialized = true;
      this.firstPage = Number(this.dataset.currentPage);
      this.lastPage = this.firstPage;
      this.nextUrl = this.dataset.nextUrl;
      this.previousUrl = this.dataset.previousUrl;
      this.automaticPages = 0;
      this.automaticLimit = 2;
      this.pages = new Set([this.firstPage]);
      this.products = new Set(Array.from(this.grid.children, (card) => this.productKey(card)));
      this.moreButton.addEventListener('click', () => this.load('next', false));
      this.previousButton.addEventListener('click', () => this.load('previous', false));
      this.controls.hidden = !this.nextUrl && !this.previousUrl;
      this.updateControls();
      if ('IntersectionObserver' in window && this.nextUrl) {
        this.observer = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.load('next', true);
        }, { rootMargin: '0px 0px 350px 0px', threshold: 0 });
        this.observe();
      }
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.controller?.abort();
    }

    productKey(card) {
      const link = card.querySelector('.rr-card__title a');
      return link ? new URL(link.getAttribute('href'), window.location.href).pathname : null;
    }

    pageUrl(value) {
      if (!value) return null;
      const current = new URL(window.location.href);
      const url = new URL(value, current);
      if (url.origin !== current.origin || url.pathname !== current.pathname) return null;
      for (const key of new Set(current.searchParams.keys())) {
        if (!['page', 'section_id', 'sections'].includes(key) && !url.searchParams.has(key)) {
          current.searchParams.getAll(key).forEach((parameter) => url.searchParams.append(key, parameter));
        }
      }
      url.hash = '';
      url.searchParams.delete('sections');
      url.searchParams.set('section_id', this.dataset.sectionId);
      return url;
    }

    updateControls() {
      this.moreButton.hidden = !this.nextUrl;
      this.previousWrap.hidden = !this.previousUrl;
      this.moreButton.disabled = Boolean(this.loading);
      this.previousButton.disabled = Boolean(this.loading);
      this.grid.setAttribute('aria-busy', this.loading ? 'true' : 'false');
    }

    observe() {
      if (!this.observer || !this.nextUrl || this.automaticPaused || this.automaticPages >= this.automaticLimit) return;
      const sentinel = this.querySelector('[data-feed-sentinel]');
      this.observer.unobserve(sentinel);
      this.observer.observe(sentinel);
    }

    async load(direction, automatic) {
      if (this.loading || !this.isConnected) return;
      if (automatic && (this.automaticPaused || this.automaticPages >= this.automaticLimit)) return;
      const value = direction === 'previous' ? this.previousUrl : this.nextUrl;
      const url = this.pageUrl(value);
      if (!url) return;
      const expectedPage = direction === 'previous' ? this.firstPage - 1 : this.lastPage + 1;
      if (this.pages.has(expectedPage)) return;
      const focusAfterLoad = !automatic && direction === 'next' && document.activeElement === this.moreButton;
      this.loading = true;
      this.dataset.feedState = 'loading';
      this.status.textContent = this.dataset.loading;
      this.updateControls();
      this.observer?.disconnect();
      this.controller = new AbortController();
      const timeout = window.setTimeout(() => this.controller.abort(), 15000);
      try {
        const response = await fetch(url.toString(), {
          credentials: 'same-origin',
          signal: this.controller.signal,
          headers: { Accept: 'text/html' }
        });
        if (!response.ok) throw new Error('Collection request failed');
        const html = await response.text();
        if (!this.isConnected) return;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const incoming = Array.from(parsed.querySelectorAll('rr-collection-feed'))
          .find((feed) => feed.dataset.sectionId === this.dataset.sectionId);
        const incomingGrid = incoming?.querySelector('[data-feed-grid]');
        if (!incomingGrid || Number(incoming.dataset.currentPage) !== expectedPage || !incomingGrid.children.length) {
          throw new Error('Collection response was incomplete');
        }
        const fragment = document.createDocumentFragment();
        let firstNewLink = null;
        for (const card of Array.from(incomingGrid.children)) {
          const key = this.productKey(card);
          if (key && !this.products.has(key)) {
            card.querySelectorAll('img').forEach((image) => { image.loading = 'lazy'; });
            const importedCard = document.importNode(card, true);
            firstNewLink ||= importedCard.querySelector('.rr-card__title a');
            fragment.appendChild(importedCard);
            this.products.add(key);
          }
        }
        const anchor = direction === 'previous' ? this.grid.firstElementChild : null;
        const anchorTop = anchor?.getBoundingClientRect().top;
        if (direction === 'previous') {
          this.grid.prepend(fragment);
          this.previousUrl = incoming.dataset.previousUrl;
          this.firstPage = expectedPage;
        } else {
          this.grid.append(fragment);
          this.nextUrl = incoming.dataset.nextUrl;
          this.lastPage = expectedPage;
          if (automatic) this.automaticPages += 1;
          const pagination = this.querySelector('[data-feed-pagination]');
          const incomingPagination = incoming.querySelector('[data-feed-pagination]');
          if (pagination && incomingPagination) {
            incomingPagination.querySelectorAll('a[href]').forEach((link) => {
              const target = new URL(link.getAttribute('href'), window.location.href);
              target.searchParams.delete('section_id');
              target.searchParams.delete('sections');
              link.setAttribute('href', target.pathname + target.search + target.hash);
            });
            pagination.replaceChildren(...Array.from(incomingPagination.childNodes, (node) => document.importNode(node, true)));
          }
        }
        this.pages.add(expectedPage);
        this.updateControls();
        if (anchor && Number.isFinite(anchorTop)) {
          const delta = anchor.getBoundingClientRect().top - anchorTop;
          if (Math.abs(delta) > 1) window.scrollBy({ top: delta, behavior: 'instant' });
        }
        this.dataset.feedState = this.nextUrl ? 'ready' : 'complete';
        this.status.textContent = direction === 'previous' ? this.dataset.previousLoaded
          : !this.nextUrl ? this.dataset.end
            : this.automaticPages >= this.automaticLimit ? this.dataset.manualHint : this.dataset.loaded;
        if (focusAfterLoad && (document.activeElement === this.moreButton || document.activeElement === document.body)) {
          firstNewLink?.focus({ preventScroll: true });
        }
      } catch (error) {
        if (!this.isConnected) return;
        this.automaticPaused = true;
        this.dataset.feedState = 'error';
        this.status.textContent = this.dataset.error;
      } finally {
        window.clearTimeout(timeout);
        this.loading = false;
        this.updateControls();
        if (this.isConnected) this.observe();
      }
    }
  }

  customElements.define('rr-collection-feed', RiotCollectionFeed);
})();
